// ============================================================================
// CREATE PROJECT MODAL
// Modal for creating a new project
// ============================================================================

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useCreateProject } from '../hooks/useProjects';
import { Project, ProjectSensitivity } from '../types';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (project: Project) => void;
}

export const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [trainingSandbox, setTrainingSandbox] = useState(false);
  const { mutate: createProject, isPending, error } = useCreateProject();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !description.trim()) {
      return;
    }

    createProject(
      {
        name: name.trim(),
        description: description.trim(),
        sensitivityLevel: ProjectSensitivity.NORMAL,
        trainingSandboxEnabled: trainingSandbox
      },
      {
        onSuccess: (project) => {
          setName('');
          setDescription('');
          setTrainingSandbox(false);
          onSuccess(project);
        }
      }
    );
  };

  const handleClose = () => {
    if (!isPending) {
      setName('');
      setDescription('');
      setTrainingSandbox(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Modal */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '90vh'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '24px',
            borderBottom: '1px solid #e9ecef',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#212529',
              margin: 0
            }}>
              Create New Project
            </h2>
            <button
              onClick={handleClose}
              disabled={isPending}
              style={{
                background: 'none',
                border: 'none',
                padding: '8px',
                cursor: isPending ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
                transition: 'background 0.2s',
                opacity: isPending ? 0.5 : 1
              }}
              onMouseOver={(e) => !isPending && (e.currentTarget.style.background = '#f8f9fa')}
              onMouseOut={(e) => (e.currentTarget.style.background = 'none')}
            >
              <X size={20} color="#868e96" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} style={{
            padding: '24px',
            flex: 1,
            overflow: 'auto'
          }}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#212529',
                marginBottom: '8px'
              }}>
                Project Name <span style={{ color: '#fa5252' }}>*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter project name"
                disabled={isPending}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid #ced4da',
                  borderRadius: '6px',
                  transition: 'border-color 0.2s',
                  outline: 'none',
                  opacity: isPending ? 0.6 : 1,
                  cursor: isPending ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => (e.target.style.borderColor = '#228be6')}
                onBlur={(e) => (e.target.style.borderColor = '#ced4da')}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#212529',
                marginBottom: '8px'
              }}>
                Description <span style={{ color: '#fa5252' }}>*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the project"
                disabled={isPending}
                required
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid #ced4da',
                  borderRadius: '6px',
                  transition: 'border-color 0.2s',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  opacity: isPending ? 0.6 : 1,
                  cursor: isPending ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => (e.target.style.borderColor = '#228be6')}
                onBlur={(e) => (e.target.style.borderColor = '#ced4da')}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#495057',
                cursor: isPending ? 'not-allowed' : 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={trainingSandbox}
                  onChange={(e) => setTrainingSandbox(e.target.checked)}
                  disabled={isPending}
                />
                Training Sandbox (practice project for facilitator training — excluded from client reporting)
              </label>
            </div>

            {error && (
              <div style={{
                padding: '12px',
                background: '#fff5f5',
                border: '1px solid #ffc9c9',
                borderRadius: '6px',
                marginBottom: '16px'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#c92a2a'
                }}>
                  Failed to create project: {error.message}
                </p>
              </div>
            )}
          </form>

          {/* Footer */}
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid #e9ecef',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px'
          }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={isPending}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                border: '1px solid #ced4da',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#495057',
                cursor: isPending ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: isPending ? 0.5 : 1
              }}
              onMouseOver={(e) => !isPending && (e.currentTarget.style.background = '#f8f9fa')}
              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isPending || !name.trim() || !description.trim()}
              style={{
                padding: '10px 20px',
                background: (!name.trim() || !description.trim() || isPending) ? '#adb5bd' : '#228be6',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                cursor: (!name.trim() || !description.trim() || isPending) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                if (!isPending && name.trim() && description.trim()) {
                  e.currentTarget.style.background = '#1971c2';
                }
              }}
              onMouseOut={(e) => {
                if (!isPending && name.trim() && description.trim()) {
                  e.currentTarget.style.background = '#228be6';
                }
              }}
            >
              {isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
